import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';

const BelaSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <Section id='belaTask bela' ref={sectionRef}>
      <Container id='bela'>
        <ContentGrid $isVisible={isVisible}>
          <ImageColumn $isVisible={isVisible}>
            <CharacterImageWrapper>
              <CharacterImage src="/BELA_1.png" alt="B.E.L.A. Character" />
              <ImageGlow />
            </CharacterImageWrapper>
          </ImageColumn>

          <TextColumn>
            <Badge $isVisible={isVisible}>
              <BadgeText>Meet B.E.L.A.</BadgeText>
            </Badge>

            <Title $isVisible={isVisible}>
              Your Planet-Positive <br />
              <TitleAccent>Companion</TitleAccent>
            </Title>

            <Description $isVisible={isVisible}>
              <b>B.E.L.A.</b> is a playful companion from the Chronicles of G.A.I.A. universe.
              Through mini-missions and interactive activities, kids develop observation
              skills, environmental awareness, and a lifelong habit of caring for our planet.
            </Description>

            <FeatureList $isVisible={isVisible}>
              <FeatureItem>
                <FeatureIcon>🎯</FeatureIcon>
                <FeatureText>
                  <FeatureTitle>Kid-Safe Activities</FeatureTitle>
                  <FeatureDesc>No login required, completely safe</FeatureDesc>
                </FeatureText>
              </FeatureItem>
              <FeatureItem>
                <FeatureIcon>🌱</FeatureIcon>
                <FeatureText>
                  <FeatureTitle>Build Agency</FeatureTitle>
                  <FeatureDesc>Develop environmental awareness</FeatureDesc>
                </FeatureText>
              </FeatureItem>
              <FeatureItem>
                <FeatureIcon>✨</FeatureIcon>
                <FeatureText>
                  <FeatureTitle>Interactive Learning</FeatureTitle>
                  <FeatureDesc>Engaging, story-driven missions</FeatureDesc>
                </FeatureText>
              </FeatureItem>
            </FeatureList>

            <StatusCard $isVisible={isVisible}>
              <StatusContent>
                <StatusTitle>The B.E.L.A. App is Coming Soon</StatusTitle>
                <StatusSubtitle>While we build, we’re sharing kid-safe, no-login experiences here.Try free activities below...</StatusSubtitle>
              </StatusContent>
            </StatusCard>
          </TextColumn>
        </ContentGrid>
      </Container>
    </Section>
  );
};

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-12px);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 0.4;
  }
  50% {
    opacity: 0.6;
  }
`;

// --- Redesigned BelaSection Styles for Modern, Minimal, Blue-Focused Look ---

const Section = styled.section`
  width: 100vw;
  background: linear-gradient(120deg, #eaf1fb 0%, #e3eafc 40%, #dbeafe 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  padding: 0;
  @media (max-width: 768px) {
    padding: 0;
  }
`;

const Container = styled.div`
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
  padding: 0 32px;

  @media (max-width: 1440px) {
    padding: 0 28px;
  }

  @media (max-width: 1280px) {
    padding: 0 24px;
  }

  @media (max-width: 1024px) {
    padding: 0 22px;
  }

  @media (max-width: 900px) {
    padding: 0 20px;
  }

  @media (max-width: 768px) {
    padding: 0 18px;
  }

  @media (max-width: 640px) {
    padding: 0 16px;
  }

  @media (max-width: 480px) {
    padding: 0 14px;
  }

  @media (max-width: 375px) {
    padding: 0 12px;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  align-items: center;
  min-height: 700px;
  padding: 100px 0 80px 0;
  opacity: ${props => props.$isVisible ? 1 : 0};
  transition: opacity 0.7s cubic-bezier(.4,0,.2,1);

  @media (max-width: 1440px) {
    gap: 35px;
    min-height: 650px;
    padding: 90px 0 70px 0;
  }

  @media (max-width: 1280px) {
    gap: 32px;
    min-height: 600px;
    padding: 80px 0 65px 0;
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 50px;
    padding: 70px 0 55px 0;
    min-height: 500px;
  }

  @media (max-width: 900px) {
    gap: 45px;
    padding: 65px 0 50px 0;
  }

  @media (max-width: 768px) {
    gap: 40px;
    padding: 60px 0 45px 0;
  }

  @media (max-width: 640px) {
    gap: 35px;
    padding: 50px 0 40px 0;
    min-height: unset;
  }

  @media (max-width: 480px) {
    gap: 30px;
    padding: 40px 0 30px 0;
  }

  @media (max-width: 375px) {
    gap: 24px;
    padding: 36px 0 24px 0;
  }
`;

const ImageColumn = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${props => props.$isVisible ? 1 : 0};
  transform: translateY(${props => props.$isVisible ? '0' : '30px'});
  transition: opacity 0.7s, transform 0.7s;
  min-width: 420px;
  min-height: 420px;
  @media (max-width: 1200px) {
    min-width: 320px;
    min-height: 320px;
  }
  @media (max-width: 600px) {
    min-width: 0;
    min-height: 0;
  }
`;

const CharacterImageWrapper = styled.div`
  position: relative;
  width: 420px;
  height: 420px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg,#ced8ff 0%,#bfccff 100%);
  border-radius: 40px;
  box-shadow: 0 8px 32px rgba(30, 64, 175, 0.10);

  @media (max-width: 1440px) {
    width: 400px;
    height: 400px;
    border-radius: 36px;
  }

  @media (max-width: 1280px) {
    width: 380px;
    height: 380px;
    border-radius: 32px;
  }

  @media (max-width: 1024px) {
    width: 360px;
    height: 360px;
    border-radius: 28px;
  }

  @media (max-width: 900px) {
    width: 320px;
    height: 320px;
    border-radius: 26px;
  }

  @media (max-width: 768px) {
    width: 280px;
    height: 280px;
    border-radius: 24px;
  }

  @media (max-width: 640px) {
    width: 250px;
    height: 250px;
    border-radius: 22px;
  }

  @media (max-width: 480px) {
    width: 220px;
    height: 220px;
    border-radius: 20px;
  }

  @media (max-width: 375px) {
    width: 200px;
    height: 200px;
    border-radius: 18px;
  }
`;

const CharacterImage = styled.img`
  width: 90%;
  height: auto;
  border-radius: 24px;
  object-fit: contain;
  z-index: 1;
  box-shadow: 0 4px 24px rgba(30, 64, 175, 0.08);
`;

const ImageGlow = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 120%;
  height: 120%;
  background: radial-gradient(circle, rgba(59,130,246) 0%, transparent 80%);
  filter: blur(32px);
  transform: translate(-50%, -50%);
  z-index: 0;
`;

const TextColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: flex-start;
  justify-content: center;
  min-width: 340px;
  @media (max-width: 1200px) {
    gap: 28px;
    min-width: 0;
  }
`;

const Badge = styled.div`
  background: linear-gradient(90deg, #2563eb 0%, #60a5fa 100%);
  color: #fff;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 999px;
  padding: 8px 28px;
  letter-spacing: 1.5px;
  box-shadow: 0 2px 8px rgba(30, 64, 175, 0.08);
  opacity: ${props => props.$isVisible ? 1 : 0};
  transform: translateY(${props => props.$isVisible ? '0' : '10px'});
  transition: opacity 0.6s, transform 0.6s;
`;

const BadgeText = styled.span`
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 1.5px;
`;

const Title = styled.h1`
  font-size: 3.2rem;
  font-weight: 900;
  line-height: 1.1;
  margin: 0 0 0px 0;
  background: linear-gradient(90deg, #1e3a8a 0%, #2563eb 60%, #60a5fa 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;
  opacity: ${props => props.$isVisible ? 1 : 0};
  transition: opacity 0.7s;

  @media (max-width: 1440px) {
    font-size: 2.9rem;
  }

  @media (max-width: 1280px) {
    font-size: 2.6rem;
  }

  @media (max-width: 1024px) {
    font-size: 2.4rem;
  }

  @media (max-width: 900px) {
    font-size: 2.2rem;
  }

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  @media (max-width: 640px) {
    font-size: 1.8rem;
  }

  @media (max-width: 480px) {
    font-size: 1.6rem;
  }

  @media (max-width: 375px) {
    font-size: 1.4rem;
  }
`;

const TitleAccent = styled.span`
  background: linear-gradient(90deg, #1e3a8a 0%, #1e40af 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;
`;

const Description = styled.p`
  font-size: 1.1rem;
  align-text: justify;
  color: #334155;
  line-height: 1.7;
  font-weight: 500;
  margin: 0 0 8px 0;
  opacity: ${props => props.$isVisible ? 1 : 0};
  transition: opacity 0.7s;

  @media (max-width: 1280px) {
    font-size: 1.05rem;
  }

  @media (max-width: 1024px) {
    font-size: 1.02rem;
  }

  @media (max-width: 900px) {
    font-size: 1rem;
    line-height: 1.65;
  }

  @media (max-width: 768px) {
    font-size: 0.98rem;
  }

  @media (max-width: 640px) {
    font-size: 0.96rem;
  }

  @media (max-width: 480px) {
    font-size: 0.94rem;
    line-height: 1.6;
  }

  @media (max-width: 375px) {
    font-size: 0.9rem;
  }
`;

const FeatureList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin: 0 0 8px 20px;
  padding: 0;
  list-style: none;
  opacity: ${props => props.$isVisible ? 1 : 0};
  transition: opacity 0.7s;
`;

const FeatureItem = styled.li`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 14px;
  background: #f1f5f9;
  border-radius: 12px;
  padding: 14px 20px;
  box-shadow: 0 2px 8px rgba(30, 64, 175, 0.04);
`;

const FeatureIcon = styled.span`
  font-size: 1rem;
  color: #2563eb;
  margin-top: 2px;
`;

const FeatureText = styled.div`
  display: flex;
  flex-direction: column;
`;

const FeatureTitle = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: #1e293b;
`;

const FeatureDesc = styled.span`
  font-size: 0.8rem;
  color: #64748b;
`;

const StatusCard = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  background: linear-gradient(90deg, #1e3a8a 0%, #1e40af 100%);
  border-radius: 10px 0 10px 0;
  padding: 18px 24px;
  box-shadow: 0 6px 20px rgba(0, 34, 255, 0.66);
  margin-top: 18px;
  opacity: ${props => props.$isVisible ? 1 : 0};
  transition: opacity 0.7s;
  color: #fff;
`;

const StatusIcon = styled.span`
  font-size: 1.6rem;
  color: #2563eb;
`;

const StatusContent = styled.div`
  display: flex;
  flex-direction: column;
  color: #fff;
`;

const StatusTitle = styled.span`
  font-size: 1.25rem;
  font-weight: 700;
  color: #fff;
`;

const StatusSubtitle = styled.span`
  font-size: 0.95rem;
  padding: 8px;
  color:rgb(237, 237, 237);
`;

export default BelaSection;